import { Injectable, signal } from '@angular/core';
import { DRAW_SCHEDULE, IDrawBase, IDrawSchedule } from '@libs/interfaces';
import { Observable, first, of, switchMap } from 'rxjs';
import { ApiService } from '../api.service';
import { IToast, MODAL_TYPE } from '@libs/store/toast/model';
import { AuthService } from '../landing/auth.service';
import { StoreBichoService } from '../store/store-bicho.service';
import { DrawsBase } from '@libs/helper/bicho/draws-base.helper';
import { DrawsSchedules } from '@libs/helper/bicho/draws-schedules.helper';

@Injectable({
  providedIn: 'root',
})
export class ScheduleService {
  /**
   * Signal to hold the list of draw bases.
   * @type {IDrawBase[]}
   */
  drawsBase = signal<IDrawBase[]>(DrawsBase);

  /**
   * Signal to hold the list of available draw schedules.
   * @type {IDrawSchedule[]}
   */
  schedules = signal<IDrawSchedule[]>(DrawsSchedules);

  constructor(
    private apiService: ApiService,
    private auth: AuthService,
    private storeBichoService: StoreBichoService,
  ) {
    this.storeBichoService.setSchedule(this.schedules()[0]);
  }
  /**
   * Sets the schedule for logged-in users.
   * @param {DRAW_SCHEDULE} schedule - The draw schedule.
   */
  onLogedSetSchedule(schedule: DRAW_SCHEDULE) {
    const _schedule = this.schedules().find(s => s.denom == schedule);
    this.storeBichoService.setSchedule(_schedule);
  }

  /**
   * Sets the selected schedule for the user.
   * @param {DRAW_SCHEDULE} schedule - The draw schedule.
   * @returns {Observable<IDrawSchedule>} An observable containing the selected schedule.
   */
  setSelectedSchedule(schedule: DRAW_SCHEDULE): Observable<IDrawSchedule> {
    const _schedule = this.schedules().find(s => s.denom == schedule);
    return this.storeBichoService.selectSchedule().pipe(
      switchMap((selected: IDrawSchedule) =>
        selected.denom == schedule ? of(_schedule) : this.updateSchedule(_schedule),
      ),
      first(),
    );
  }

  /**
   * Updates the schedule for the user.
   * @param {IDrawSchedule} schedule - The draw schedule.
   * @returns {Observable<IDrawSchedule>} An observable containing the updated schedule.
   */
  private updateSchedule(schedule: IDrawSchedule) {
    this.toastSchedule();
    return this.auth.logged() ? this.updateScheduleStoreLogged(schedule) : this.updateScheduleStoreNotLogged(schedule);
  }

  /**
   * Updates the schedule in the store for logged-in users.
   * @param {IDrawSchedule} schedule - The draw schedule.
   * @returns {Observable<IDrawSchedule>} An observable containing the updated schedule.
   */
  private updateScheduleStoreLogged(schedule: IDrawSchedule): Observable<IDrawSchedule> {
    this.storeBichoService.setUserSelectedSchedule(schedule.denom);
    this.storeBichoService.setSchedule(schedule);
    this.apiService.patch(`user/schedule/${schedule.denom}`).pipe(first()).subscribe();
    return of(schedule);
  }

  /**
   * Updates the schedule in the store for not logged-in users.
   * @param {IDrawSchedule} schedule - The draw schedule.
   * @returns {Observable<IDrawSchedule>} An observable containing the updated schedule.
   */
  updateScheduleStoreNotLogged(schedule: IDrawSchedule): Observable<IDrawSchedule> {
    this.storeBichoService.setSchedule(schedule);
    return of(schedule); // Return the updated schedule
  }

  /**
   * Displays a toast message for the schedule update.
   */
  private toastSchedule() {
    const toast: IToast = {
      id: Date.now(),
      info: 'APP.CSD', // Information message for the toast
      type: MODAL_TYPE.WARNING, // Type of the toast message
      exp: 2000, // Expiration time for the toast message
    };
    this.storeBichoService.addToast(toast); // Dispatch an action to add the toast message
  }

  /**
   * Gets the image URL for the given schedule.
   * @param {DRAW_SCHEDULE} schedule - The draw schedule.
   * @returns {string} The URL of the image.
   */
  getScheduleImageUrl(schedule: DRAW_SCHEDULE): string {
    switch (schedule) {
      case DRAW_SCHEDULE.RIO:
        return 'url("/assets/images/rio-bg.webp")';
      case DRAW_SCHEDULE.ASIA:
        return 'url("/assets/images/asia-bg.webp")';
      case DRAW_SCHEDULE.EUROPE:
        return 'url("/assets/images/europa-bg.webp")';
      case DRAW_SCHEDULE.LBR:
        return 'url("/assets/images/lbr-bg.webp")';
      case DRAW_SCHEDULE.LDB24:
        return 'url("/assets/images/lb24-bg.webp")';
      case DRAW_SCHEDULE.LOOK:
        return 'url("/assets/images/lg-bg.webp")';
      case DRAW_SCHEDULE.LDBEXPRESS:
        return 'url("/assets/images/express-bg.webp")';
      case DRAW_SCHEDULE.LN:
        return 'url("/assets/images/ln-bg.webp")';
      case DRAW_SCHEDULE.SOUTH_AMERICA:
        return 'url("/assets/images/sul-america-bg.webp")';
    }
  }
}
